namespace SolovisGateway.Admin.Organizations {

    export const DisableConnectionOutcome = {
        Disabled: "Disabled",
        AlreadyDisabled: "AlreadyDisabled",
        BackendError: "BackendError",
        DisabledButNotConfirmed: "DisabledButNotConfirmed",
    } as const;
    export type DisableConnectionOutcome = typeof DisableConnectionOutcome[keyof typeof DisableConnectionOutcome];

    export interface OrgDisableConnectionDialogOptions extends SupportContactOptions {
        connectionName: string;
        domains: Array<string>;
        onDisable?: () => void;
        onCancel?: () => void;
    }

    export class OrgDisableConnectionDialog {

        private _options: OrgDisableConnectionDialogOptions;

        private _dialog: any;
        private _confirmContainer: JQuery;
        private _loaderContainer: JQuery;
        private _successContainer: JQuery;
        private _failureContainer: JQuery;
        private _partialContainer: JQuery;

        constructor(options: OrgDisableConnectionDialogOptions) {
            this._options = options;
        }

        public show() {
            var body = $("<div>").css({ "padding": "0 15px" });

            var confirmContainer = $("<div>");
            body.append(confirmContainer);
            this._confirmContainer = confirmContainer;

            var hasDomains = this._options.domains && this._options.domains.length > 0;

            if (hasDomains) {
                confirmContainer.append($("<p>").append(
                    $("<span>").text("Disabling "),
                    $("<strong>").text(this._options.connectionName),
                    $("<span>").text(" affects these domains:")
                ));

                var domainsBox = $("<div>").css({ "border": "1px solid #ddd", "margin-bottom": "15px" });
                this._options.domains.forEach(function (domain, index) {
                    domainsBox.append(
                        $("<div>").css({
                            "padding": "10px 15px",
                            "background-color": index % 2 === 0 ? "#ffffff" : "#f5f5f5"
                        }).text(domain)
                    );
                });
                confirmContainer.append(domainsBox);

                confirmContainer.append($("<p>").text(
                    "Users on these domains will sign in with a username and password instead of SSO."
                ));

                var resetPasswordNote = $("<p>");
                resetPasswordNote.append($("<span>").text("Users will need to reset their password from the sign-in page. "));
                SsoConnectionStatusUi.appendSupportContactLine(
                    resetPasswordNote,
                    this._options,
                    "Should they encounter any issues, ",
                    "."
                );
                confirmContainer.append(resetPasswordNote);

                confirmContainer.append($("<p>").text(
                    "Their access, roles, and environments do not change. Only the way they sign in changes."
                ));
            }
            else {
                confirmContainer.append($("<p>").append(
                    $("<span>").text("Disabling "),
                    $("<strong>").text(this._options.connectionName),
                    $("<span>").text(".")
                ));

                confirmContainer.append($("<p>").text(
                    "This connection has no email domains assigned. No users are affected. Disabling it only turns the connection off."
                ));
            }

            var loaderContainer = $("<div>")
                .css({ "position": "relative", "height": "150px", "display": "none" });
            Solovis.UI.StockAnimations.createSpinningCircles({
                diameter: "60px",
                top: "50%",
                left: "50%",
                showShadow: true
            }).appendTo(loaderContainer);
            body.append(loaderContainer);
            this._loaderContainer = loaderContainer;

            var successContainer = $("<div>").css({ "display": "none" });
            successContainer.append($("<div>").css({
                "background-color": "#dff0d8",
                "border": "1px solid #d6e9c6",
                "color": "#3c763d",
                "padding": "12px 15px",
                "border-radius": "4px"
            }).text("The connection is disabled."));
            body.append(successContainer);
            this._successContainer = successContainer;

            var failureContainer = $("<div>").css({ "display": "none" });
            failureContainer.append($("<div>").css({
                "background-color": "#f2dede",
                "border": "1px solid #ebccd1",
                "color": "#a94442",
                "padding": "12px 15px",
                "border-radius": "4px"
            }).text("We could not disable the connection. Please try again."));
            body.append(failureContainer);
            this._failureContainer = failureContainer;

            var partialContainer = $("<div>").css({ "display": "none" });
            partialContainer.append($("<div>").css({
                "background-color": "#fcf8e3",
                "border": "1px solid #faebcc",
                "color": "#8a6d3b",
                "padding": "12px 15px",
                "border-radius": "4px"
            }).text(
                "The connection was disabled with your identity provider, but Solovis could not " +
                "confirm the change. It may still show as Enabled until this is corrected. Click " +
                "Disable again to finish."
            ));
            body.append(partialContainer);
            this._partialContainer = partialContainer;

            this._dialog = new (Solovis as any).UI.Dialog({
                container: body,
                title: "Do you want to disable this connection?",
                modal: true,
                autoOpen: true,
                center: true,
                destroyOnClose: true,
                actions: ["Close"],
                width: 600,
                buttons: this._buildDefaultButtons()
            });
        }

        public showSuccess(onClosed?: () => void) {
            this._loaderContainer.hide();
            this._failureContainer.hide();
            this._partialContainer.hide();
            this._successContainer.show();
            this._dialog.updateButtons([
                {
                    label: "OK",
                    click: () => {
                        this._dialog.close();
                        if (onClosed) {
                            onClosed();
                        }
                    }
                }
            ]);
        }

        public showFailure() {
            this._loaderContainer.hide();
            this._successContainer.hide();
            this._partialContainer.hide();
            this._failureContainer.show();
            this._dialog.updateButtons(this._buildDefaultButtons());
        }

        // DisableConnectionOutcome.DisabledButNotConfirmed: Auth0 already disabled the connection, but the Gateway DB
        // update failed afterward. Distinct from showFailure() because retrying here isn't "try
        // the same thing again" — it's the only way to close the gap between what Auth0 has
        // already done and what the Gateway's own record still says.
        public showPartialFailure() {
            this._loaderContainer.hide();
            this._successContainer.hide();
            this._failureContainer.hide();
            this._partialContainer.show();
            this._dialog.updateButtons(this._buildDefaultButtons());
        }

        public resetToConfirm() {
            this._loaderContainer.hide();
            this._successContainer.hide();
            this._failureContainer.hide();
            this._partialContainer.hide();
            this._confirmContainer.show();
            this._dialog.updateButtons(this._buildDefaultButtons());
        }

        private _buildDefaultButtons() {
            var self = this;
            return [
                {
                    label: "Disable",
                    click: function () {
                        self._attemptDisable();
                    }
                },
                {
                    label: "Cancel",
                    click: function () {
                        self._dialog.close();
                        if (self._options.onCancel) {
                            self._options.onCancel();
                        }
                    }
                }
            ];
        }

        private _attemptDisable() {
            this._confirmContainer.hide();
            this._failureContainer.hide();
            this._loaderContainer.show();
            this._dialog.updateButtons([]);

            if (this._options.onDisable) {
                this._options.onDisable();
            }
        }
    }
}
